<template>
  <div v-if="loading" class="row" style="background-color: #fff;">
    <div class="col-12">
      <div class="loader">
        <b-spinner
          variant="primary"
          type="grow"
          :label="`${$t('reports.detractor.loadingLabel')}...`"
        ></b-spinner>
      </div>
    </div>
  </div>
  <div v-else class="row">
    <div class="col-12">
      <div class="reports-wrapper">
        <div class="card" style="width: 31%;">
          <h1>{{ $t('reports.sentmentAnalysis.reportTitle') }}</h1>
          <p>{{ $t('reports.sentmentAnalysis.reportDescription') }}</p>
        </div>
        <div class="card" style="width: 16.5%;">
          <h1>{{ $t('reports.sentmentAnalysis.positive') }}</h1>
          <div class="sentiment-area">
            <img src="../../../../assets/positive.gif" alt="positive" />
            <span style="font-size: 32px; font-weight: bold;">{{
              data.positiveQty
            }}</span>
          </div>
        </div>
        <div class="card" style="width: 16.5%;">
          <h1>{{ $t('reports.sentmentAnalysis.neutral') }}</h1>
          <div class="sentiment-area">
            <img src="../../../../assets/neutral.gif" alt="neutral" />
            <span style="font-size: 32px; font-weight: bold;">{{
              data.neutralQty
            }}</span>
          </div>
        </div>
        <div class="card" style="width: 16.5%;">
          <h1>{{ $t('reports.sentmentAnalysis.negative') }}</h1>
          <div class="sentiment-area">
            <img src="../../../../assets/negative.gif" alt="negative" />
            <span style="font-size: 32px; font-weight: bold;">{{
              data.negativeQty
            }}</span>
          </div>
        </div>
        <div class="card" style="width: 16.5%;">
          <h1>{{ $t('reports.sentmentAnalysis.total') }}</h1>
          <div class="sentiment-area" style="flex-direction: column;">
            <span style="font-size: 32px; font-weight: bold;">
              {{ data.totalAnswersAnalyzed }}
            </span>
            <p style="font-size: 12px;">
              {{
                `${(
                  (data.totalAnswersAnalyzed / data.totalAnswers) *
                  100
                ).toFixed(2)}% ${$t(
                  'reports.sentmentAnalysis.percentAnalyzed',
                )}`
              }}
            </p>
          </div>
        </div>
      </div>

      <div class="reports-wrapper" style="margin-top: 16px;">
        <div class="card" style="width: 31%;">
          <h1>
            {{ $t('reports.sentmentAnalysis.classificationReportTitle') }}
          </h1>
          <highcharts :options="chartOptionsPie"></highcharts>
        </div>
        <div class="card" style="width: 68.2%;">
          <h1>
            {{
              $t('reports.sentmentAnalysis.classificationReportTitleMonthly')
            }}
          </h1>
          <highcharts :options="chartOptionsLine"></highcharts>
        </div>
      </div>

      <div class="reports-wrapper" style="margin-top: 16px;">
        <div class="card" style="width: 31%;">
          <h1>
            {{ $t('reports.sentmentAnalysis.sentmentScoreReportTitle') }}
          </h1>
          <div class="chart-area">
            <radial-progress-bar
              :diameter="240"
              :stroke-width="20"
              innerStrokeColor="#f1f1f1"
              :startColor="data.scoreColor"
              :stopColor="data.scoreColor"
              :completed-steps="data.score"
              :total-steps="100"
            >
              <b
                :style="{
                  color: data.scoreColor,
                  fontSize: '2.8em',
                  fontWeight: 'bold',
                }"
              >
                {{ data.score }}
              </b>
            </radial-progress-bar>
          </div>
        </div>
        <div class="card" style="width: 68.2%;">
          <h1>
            {{ $t('reports.sentmentAnalysis.sentmentScoreReportTitleMonthly') }}
          </h1>
          <highcharts :options="chartOptions"></highcharts>
        </div>
      </div>

      <div class="reports-wrapper" style="margin-top: 16px;">
        <div class="card" style="width: 100%;">
          <h1>
            Top 5 keywords
          </h1>
          <div class="row">
            <div class="col-4">
              <b-table
                striped
                responsive
                id="table-positive"
                :items="data.positiveKeyWords"
                :fields="fieldsPositive"
              >
                <template v-slot:table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner type="grow" class="align-middle"></b-spinner>
                  </div>
                </template>
                <template v-slot:cell(progress)="row">
                  <div style="min-width: 70px;">
                    <b-progress
                      variant="primary"
                      :value="row.item.percentage"
                      :max="100"
                    ></b-progress>
                  </div>
                </template>
                <template v-slot:cell(percentage)="row">
                  {{ row.item.percentage + '%' }}
                </template>
              </b-table>
            </div>
            <div class="col-4">
              <b-table
                striped
                responsive
                id="table-neutral"
                :items="data.neutralKeyWords"
                :fields="fieldsNeutral"
              >
                <template v-slot:table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner type="grow" class="align-middle"></b-spinner>
                  </div>
                </template>
                <template v-slot:cell(progress)="row">
                  <div style="min-width: 70px;">
                    <b-progress
                      variant="warning"
                      :value="row.item.percentage"
                      :max="100"
                    ></b-progress>
                  </div>
                </template>
                <template v-slot:cell(percentage)="row">
                  {{ row.item.percentage + '%' }}
                </template>
              </b-table>
            </div>
            <div class="col-4">
              <b-table
                striped
                responsive
                id="table-negative"
                :items="data.negativeKeyWords"
                :fields="fieldsNegative"
              >
                <template v-slot:table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner type="grow" class="align-middle"></b-spinner>
                  </div>
                </template>
                <template v-slot:cell(progress)="row">
                  <div style="min-width: 70px;">
                    <b-progress
                      variant="danger"
                      :value="row.item.percentage"
                      :max="100"
                    ></b-progress>
                  </div>
                </template>
                <template v-slot:cell(percentage)="row">
                  {{ row.item.percentage + '%' }}
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>

      <div class="reports-wrapper" style="margin-top: 16px;">
        <div class="card" style="width: 100%;">
          <label for="select-indicator">{{
            $t('reports.sentmentAnalysis.selectTitle')
          }}</label>
          <div style="width: 250px;">
            <b-form-select
              v-model="indicatorSelected"
              id="select-indicator"
              :options="indicatorLabels"
            ></b-form-select>
          </div>

          <div v-if="indicatorSelected" class="mt-4">
            <b-table
              striped
              responsive
              id="table-positive"
              :items="indicatorsItems"
              :fields="indicatorsFields"
              :busy="loadingIndicators"
              :per-page="10"
              :current-page="currentPage"
            >
              <template v-slot:table-busy>
                <div class="text-center text-primary my-2">
                  <b-spinner type="grow" class="align-middle"></b-spinner>
                </div>
              </template>
              <template v-slot:cell(progress)="row">
                <div style="min-width: 70px;">
                  <b-progress class="mt-1" :max="row.item.total" show-value>
                    <b-progress-bar
                      :value="row.item.positiveQty"
                      variant="primary"
                    ></b-progress-bar>
                    <b-progress-bar
                      :value="row.item.neutralQty"
                      variant="warning"
                    ></b-progress-bar>
                    <b-progress-bar
                      :value="row.item.negativeQty"
                      variant="danger"
                    ></b-progress-bar>
                  </b-progress>
                </div>
              </template>
            </b-table>

            <div class="mt-4">
              <b-pagination
                v-model="currentPage"
                :total-rows="indicatorsItems.length"
                :per-page="10"
                aria-controls="table-sentment-by-indicators"
              ></b-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import RadialProgressBar from 'vue-radial-progress';
import Indicators from '../../../molecules/highcharts/reports/indicators';
import api from '../../../../services/api';

export default {
  components: {
    RadialProgressBar,
    Indicators,
  },

  data: () => ({
    loading: true,
    data: {
      totalAnswers: 0,
      totalAnswersAnalyzed: 0,
      positiveQty: 0,
      neutralQty: 0,
      negativeQty: 0,
      score: 0,
      scoreColor: '#c1c1c1',
      positiveKeyWords: [],
      neutralKeyWords: [],
      negativeKeyWords: [],
    },
    chartOptionsLine: {
      chart: {
        type: 'spline',
        style: {
          width: '100%',
          height: '350px',
        },
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: [],
        visible: true,
        labels: {},
      },
      yAxis: {
        title: {
          text: '',
        },
        stackLabels: {
          enabled: true,
          fontWeight: 'bold',
        },
        max: 100,
      },
      tooltip: {
        pointFormat:
          '<span style="color:{series.color}">Quantidade</span>: <b>{point.qty}</b> ({point.y}%)<br/>',
      },
      plotOptions: {
        spline: {
          lineWidth: 3,
        },
        column: {
          pointPadding: 0.1,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
          },
        },
        line: {
          dataLabels: {
            enabled: true,
          },
        },
        area: {
          dataLabels: {
            enabled: true,
          },
        },
        series: {
          groupPadding: 0,
          pointPadding: 0.05,
          borderWidth: 0,
        },
      },
      colors: ['#22CD70', '#F3C102', '#E84C3E'],
      series: [
        {
          name: 'Positivo',
          data: [],
        },
        {
          name: 'Neutro',
          data: [],
        },
        {
          name: 'Negativo',
          data: [],
        },
      ],
    },
    chartOptionsPie: {
      title: {
        text: '',
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f}%',
          },
          showInLegend: true,
        },
      },
      colors: ['#22CD70', '#F3C102', '#E84C3E'],
      series: [
        {
          innerSize: '50%',
          name: 'Quantidade',
          data: [
            {
              name: 'Positivo',
              y: 0,
            },
            {
              name: 'Neutro',
              y: 0,
            },
            {
              name: 'Negativo',
              y: 0,
            },
          ],
        },
      ],
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        type: 'pie',
        style: {
          fontSize: '12px',
          width: '100%',
          height: '350px',
        },
      },
    },
    chartOptions: {
      title: {
        align: 'left',
        text: '',
        style: {
          color: '#5f6368',
        },
      },
      chart: {
        type: 'spline',
        borderColor: 'rgba(0, 0, 0, 0.09)',
        style: {
          width: '100%',
          height: '350px',
        },
      },
      xAxis: {
        categories: [],
        visible: true,
        labels: {},
      },
      yAxis: {
        // gridLineWidth: 0,
        // labels: {
        //   enabled: false,
        // },
        // max: 90,
        title: {
          text: '',
        },
        stackLabels: {
          enabled: true,
          fontWeight: 'bold',
        },
      },
      series: [
        {
          name: 'Score',
          data: [],
        },
      ],
      legend: {
        enabled: false,
      },
      plotOptions: {
        spline: {
          lineWidth: 3,
        },
        column: {
          pointPadding: 0.1,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
          },
        },
        line: {
          dataLabels: {
            enabled: true,
          },
        },
        area: {
          dataLabels: {
            enabled: true,
          },
        },
        series: {
          groupPadding: 0,
          pointPadding: 0.05,
          borderWidth: 0,
        },
      },
      colors: ['#4ce595'],
    },
    fieldsPositive: [
      { key: 'word', label: 'Keywords positivas' },
      {
        key: 'progress',
        label: '',
      },
      {
        key: 'percentage',
        label: 'Score',
      },
      {
        key: 'occurrences',
        label: 'Total',
      },
    ],
    fieldsNeutral: [
      { key: 'word', label: 'Keywords neutras' },
      {
        key: 'progress',
        label: '',
      },
      {
        key: 'percentage',
        label: 'Score',
      },
      {
        key: 'occurrences',
        label: 'Total',
      },
    ],
    fieldsNegative: [
      { key: 'word', label: 'Keywords negativas' },
      {
        key: 'progress',
        label: '',
      },
      {
        key: 'percentage',
        label: 'Score',
      },
      {
        key: 'occurrences',
        label: 'Total',
      },
    ],
    indicatorsFields: [
      { key: 'indicator', label: 'Indicador' },
      { key: 'value', label: 'Descrição', sortable: true },
      {
        key: 'progress',
        label: '',
      },
      {
        key: 'score',
        label: 'Score',
        class: 'text-center numbers-area',
        sortable: true,
      },
      {
        key: 'total',
        label: 'Total',
        class: 'text-center numbers-area',
        sortable: true,
      },
    ],
    indicatorSelected: null,
    indicatorLabels: [],
    indicatorsItems: [],
    currentPage: 1,
    loadingIndicators: false,
  }),

  computed: {
    ...mapState('actions', ['myActionId', 'actionGroupData', 'myActionsIds']),
    ...mapState('branches', ['myIh1', 'myIh2', 'myHierarchies1', 'myHierarchies2']),
    ...mapState('companies', ['myMetric']),
    ...mapState('calendar', ['calendarDates']),
    ...mapState('indicators', ['indicatorName', 'indicatorData']),
  },

  methods: {
    async getReportsData() {
      this.loading = true;

      this.indicatorsItems = [];
      this.indicatorSelected = null;

      await Promise.all([this.getSentmentAnalysisInfo(), this.getIndicators()]);

      this.loading = false;
    },

    async getIndicators() {
      this.indicatorSelected = null;
      this.indicatorLabels = [
        {
          value: null,
          text: this.$t('reports.sentmentAnalysis.selectPlaceholder'),
        },
      ];

      const response = await api.get(
        `indicators/distinct/${this.userAuth.companyId}`,
        {
          headers: {
            'x-access-token': this.userAuth.token,
          },
          params: {
            actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
            groupId: this.actionGroupData._id,
            metric: this.myMetric ? this.myMetric : null,
            ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
            ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
          },
        },
      );

      response.data.indicators.forEach((indicator) => {
        this.indicatorLabels.push({
          value: indicator.key,
          text: indicator.text,
        });
      });
    },

    async getSentmentAnalysisInfo() {
      const response = await api.get('/reports/sentment-analysis', {
        headers: {
          'x-access-token': this.userAuth.token,
        },
        params: {
          actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
          startDate: this.calendarDates.start,
          endDate: this.calendarDates.end,
          ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
          ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
          metric: this.myMetric,
          groupId: this.actionGroupData._id,
          indicator: this.indicatorName,
          indicatorValue: this.indicatorData.value,
          indicatorRange: this.indicatorData.range,
        },
      });

      this.data.totalAnswers = response.data.totalAnswers;
      this.data.totalAnswersAnalyzed = response.data.totalAnswersAnalyzed;

      this.data.positiveQty = response.data.qty.positive;
      this.data.neutralQty = response.data.qty.neutral;
      this.data.negativeQty = response.data.qty.negative;
      this.data.score = response.data.score.total;
      this.data.scoreColor = this.setColor(response.data.score.total);

      this.chartOptionsPie.series[0].data[0].y = this.data.positiveQty;
      this.chartOptionsPie.series[0].data[1].y = this.data.neutralQty;
      this.chartOptionsPie.series[0].data[2].y = this.data.negativeQty;

      this.chartOptionsLine.xAxis.categories = response.data.months;
      this.chartOptionsLine.series[0].data = response.data.qty.positivePerMonth;
      this.chartOptionsLine.series[1].data = response.data.qty.neutralPerMonth;
      this.chartOptionsLine.series[2].data = response.data.qty.negativePerMonth;

      this.chartOptions.xAxis.categories = response.data.months;
      this.chartOptions.series[0].data = response.data.score.perMonth;

      this.data.positiveKeyWords = response.data.keyWords.positive;
      this.data.neutralKeyWords = response.data.keyWords.neutral;
      this.data.negativeKeyWords = response.data.keyWords.negative;
    },

    async getSentmentAnalysisByIndicators() {
      this.loadingIndicators = true;

      const response = await api.get('/reports/sentment-analysis/indicator', {
        headers: {
          'x-access-token': this.userAuth.token,
        },
        params: {
          actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
          startDate: this.calendarDates.start,
          endDate: this.calendarDates.end,
          ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
          ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
          metric: this.myMetric,
          groupId: this.actionGroupData._id,
          indicator: this.indicatorSelected,
        },
      });

      this.indicatorsItems = response.data;

      this.loadingIndicators = false;
    },

    setColor(score) {
      if (score >= 60) {
        return '#22CD70';
      } else if (score >= 0) {
        return '#F3C102';
      }

      return '#E84C3E';
    },

    changeLabels() {
      this.fieldsPositive = [
        {
          key: 'word',
          label: this.$t(
            'reports.sentmentAnalysis.keywordsTablePositive.keywords',
          ),
        },
        {
          key: 'progress',
          label: '',
        },
        {
          key: 'percentage',
          label: this.$t(
            'reports.sentmentAnalysis.keywordsTablePositive.percentage',
          ),
        },
        {
          key: 'occurrences',
          label: this.$t(
            'reports.sentmentAnalysis.keywordsTablePositive.total',
          ),
        },
      ];

      this.fieldsNeutral = [
        {
          key: 'word',
          label: this.$t(
            'reports.sentmentAnalysis.keywordsTableNeutral.keywords',
          ),
        },
        {
          key: 'progress',
          label: '',
        },
        {
          key: 'percentage',
          label: this.$t(
            'reports.sentmentAnalysis.keywordsTableNeutral.percentage',
          ),
        },
        {
          key: 'occurrences',
          label: this.$t('reports.sentmentAnalysis.keywordsTableNeutral.total'),
        },
      ];

      this.fieldsNegative = [
        {
          key: 'word',
          label: this.$t(
            'reports.sentmentAnalysis.keywordsTableNegative.keywords',
          ),
        },
        {
          key: 'progress',
          label: '',
        },
        {
          key: 'percentage',
          label: this.$t(
            'reports.sentmentAnalysis.keywordsTableNegative.percentage',
          ),
        },
        {
          key: 'occurrences',
          label: this.$t(
            'reports.sentmentAnalysis.keywordsTableNegative.total',
          ),
        },
      ];

      this.indicatorsFields = [
        {
          key: 'indicator',
          label: this.$t('reports.sentmentAnalysis.indicatorTable.indicator'),
        },
        {
          key: 'value',
          label: this.$t('reports.sentmentAnalysis.indicatorTable.description'),
          sortable: true,
        },
        {
          key: 'progress',
          label: '',
        },
        {
          key: 'score',
          label: this.$t('reports.sentmentAnalysis.indicatorTable.score'),
          class: 'text-center numbers-area',
          sortable: true,
        },
        {
          key: 'total',
          label: this.$t('reports.sentmentAnalysis.indicatorTable.total'),
          class: 'text-center numbers-area',
          sortable: true,
        },
      ];

      this.indicatorLabels = [
        {
          value: null,
          text: this.$t('reports.sentmentAnalysis.selectPlaceholder'),
        },
      ];
    },
  },

  watch: {
    indicatorSelected: async function watchIndicatorSelected(value) {
      if (value) {
        this.getSentmentAnalysisByIndicators();
      } else {
        this.indicatorsItems = [];
        this.indicatorSelected = null;
      }
    },

    actionGroupData: function watchActionGroup() {
      this.getReportsData();
    },

    myActionId: function watchmyActionId() {
      this.getReportsData();
    },

    myActionsIds: function watchmyActionsIds() {
      this.getReportsData();
    },

    calendarDates: function watchCalendarDates() {
      this.getReportsData();
    },

    myIh1: function watchMyIh1() {
      this.getReportsData();
    },

    myHierarchies1: function watchMyHierarchies1() {
      this.getReportsData();
    },

    myIh2: function watchMyIh2() {
      this.getReportsData();
    },

    myHierarchies2: function watchMyHierarchies2() {
      this.getReportsData();
    },

    myMetric: function watchMyMetric() {
      this.getReportsData();
    },

    'indicatorData.value': function watchIndicatorDataValue() {
      this.getReportsData();
    },
    'indicatorData.period': async function watchIndicatorPeriod() {
      await this.getReportsData();
    },

    '$i18n.locale': function watchLocale() {
      this.changeLabels();
    },
  },

  async created() {
    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));
    this.changeLabels();
    await this.getReportsData();
  },
};
</script>

<style scoped lang="scss">
.loader {
  width: 100%;
  height: 350px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.reports-wrapper {
  width: 100%;
  display: flex;
  overflow: hidden;
  flex-direction: row;
  justify-content: space-between;
}

.card {
  background-color: #fff;
  padding: 16px;

  h1 {
    font-size: 22px;
  }

  .sentiment-area {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-around;
  }
}

.page-spacing {
  padding-left: 15px;
  padding-right: 15px;
}

.graphic-card {
  display: flex;
  flex-direction: column;
  padding: 15px 35px;
  border: 1px solid #eee;
  height: 100%;
}

.chart-area {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.text-color {
  color: #5f6368;
  font-size: 18px;
  fill: #5f6368;
}

.highcharts-background-pie {
  width: 0;
}

.numbers-area {
  max-width: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/molecules/highcharts/sentment-analysis/index.vue